import { additionalFlags } from './additionalFlags';
import { AzureVariable } from './envVariablesSchema';

const logEnvVariableError = (key: string, message: string): void => {
  console.error(`${message}\nFor key: ${key}`);
};

const envVariablesErrorMessages = {
  commaUsedInNumber: `Use dot instead of comma as the decimal seperator.`,
  inproperBooleanValue: (value: string): string =>
    `Boolean values should be passed down as "true" or "false". Received: ${value} - defaulting to false.`,
  noKeyInSchema: `The provided environmental variable doesn't exist in envVariablesSchema.`,
  parsedNaN: (value: string): string =>
    `Parsing the following value ${value} resulted in NaN.`,
  unhandledValueType: (valueType: string): string =>
    `The specified variable env type is not handled: "${valueType}".`
};

function parseValue(
  key: string,
  value: string,
  valueType: typeof AzureVariable | undefined
): string | number | boolean | undefined {
  switch (valueType) {
    case AzureVariable.string:
      return value;

    case AzureVariable.number:
      if (value.includes(',')) {
        logEnvVariableError(key, envVariablesErrorMessages.commaUsedInNumber);
      }
      if (Number.isNaN(Number.parseFloat(value))) {
        logEnvVariableError(key, envVariablesErrorMessages.parsedNaN(value));
      }
      return Number.parseFloat(value);

    case AzureVariable.boolean:
      if (!['true', 'false'].includes(value)) {
        logEnvVariableError(
          key,
          envVariablesErrorMessages.inproperBooleanValue(value)
        );
      }
      return value.toLowerCase() === 'true' ? true : false;
    case undefined:
      logEnvVariableError(key, envVariablesErrorMessages.noKeyInSchema);
      return undefined;

    default:
      logEnvVariableError(
        key,
        envVariablesErrorMessages.unhandledValueType(valueType)
      );
      return undefined;
  }
}

export function processEnvVariables(
  rawEnvVariables: Record<string, string>,
  schema: Record<string, typeof AzureVariable>
): Record<string, any> {
  const parsedEnvVariables = Object.entries(rawEnvVariables).reduce(
    (acc, [key, stringValue]) => ({
      ...acc,
      [key]: parseValue(key, stringValue, schema[key])
    }),
    {} as Record<string, any>
  );
  return parsedEnvVariables;
}

export function addFlags(
  envVariables: Record<string, any>
): Record<string, any> {
  const processedAdditionalFlags = Object.entries(additionalFlags).reduce(
    (acc, [key, flagFunction]) => ({
      ...acc,
      [key]: (flagFunction as (env: Record<string, any>) => any)(envVariables)
    }),
    {} as Record<string, any>
  );

  return {
    ...envVariables,
    ...processedAdditionalFlags
  };
}
